<template>
  <div class="premium-input-wrapper" :class="{ 'has-error': error }">
    <label v-if="label" class="premium-label">{{ label }}</label>
    <v-textarea
      v-if="isTextarea"
      v-bind="$attrs"
      v-on="$listeners"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      :rules="rules"
      :error="!!error"
      :error-messages="error"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-textarea>
    <div v-else-if="isSelect && searchable" class="autocomplete-wrapper">
      <v-autocomplete
        v-bind="attrsWithoutPlaceholder"
        v-on="$listeners"
        outlined
        dense
        hide-details="auto"
        class="premium-input"
        :items="items"
        :item-text="itemText"
        :item-value="itemValue"
        :prepend-inner-icon="icon"
        :rules="rules"
        :error="!!error"
        :error-messages="error"
        color="var(--accent)"
        clearable
        no-data-text="No results found"
        @focus="autocompleteOpen = true"
        @blur="autocompleteOpen = false"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip
            v-if="$attrs.multiple !== undefined && $attrs.multiple !== false"
            small
            close
            @click:close="removeChip(index)"
          >{{ typeof item === 'object' ? item[itemText] : item }}</v-chip>
          <span v-else>{{ typeof item === 'object' ? item[itemText] : item }}</span>
        </template>
        <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
          <slot :name="slotName" />
        </template>
      </v-autocomplete>
      <!-- Overlay placeholder — shown only when no value selected and not focused -->
      <span
        v-if="!hasValue && !autocompleteOpen"
        class="autocomplete-placeholder"
      >{{ placeholder }}</span>
    </div>
    <v-select
      v-else-if="isSelect"
      v-bind="$attrs"
      v-on="$listeners"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :placeholder="placeholder"
      :items="items"
      :item-text="itemText"
      :item-value="itemValue"
      :prepend-inner-icon="icon"
      :rules="rules"
      :error="!!error"
      :error-messages="error"
      color="var(--accent)"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-select>
    <v-text-field
      v-else
      v-bind="$attrs"
      v-on="$listeners"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :placeholder="placeholder"
      :type="type"
      :prepend-inner-icon="icon"
      :rules="rules"
      :error="!!error"
      :error-messages="error"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-text-field>
  </div>
</template>

<script>
export default {
  name: 'PremiumInput',
  inheritAttrs: false,
  data() {
    return {
      autocompleteOpen: false,
    }
  },
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    icon: { type: String, default: '' },
    type: { type: String, default: 'text' },
    isTextarea: { type: Boolean, default: false },
    isSelect: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    itemText: { type: String, default: 'label' },
    itemValue: { type: String, default: 'value' },
    rules: { type: Array, default: () => [] },
    error: { type: String, default: '' }
  },
  computed: {
    hasValue() {
      const v = this.$attrs.value
      if (Array.isArray(v)) return v.length > 0
      return v !== null && v !== undefined && v !== ''
    },
    attrsWithoutPlaceholder() {
      // eslint-disable-next-line no-unused-vars
      const { placeholder: _p, ...rest } = this.$attrs
      return rest
    }
  },
  methods: {
    removeChip(index) {
      const val = Array.isArray(this.$attrs.value)
        ? [...this.$attrs.value]
        : []
      val.splice(index, 1)
      this.$emit('input', val)
    }
  }
}
</script>

<style scoped>
.premium-input-wrapper {
  margin-bottom: 20px;
}
.premium-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 6px;
  font-family: "Inter", sans-serif;
}
.premium-input >>> .v-input__slot {
  border-radius: 8px !important;
  background: var(--bg-primary) !important;
  transition: all 0.2s ease;
  min-height: 40px !important;
}
.premium-input {
  padding-top: 0 !important;
  margin-top: 0 !important;
}
.premium-input >>> fieldset {
  border-color: var(--border-color2) !important;
}
.premium-input:hover:not(.v-input--disabled) >>> fieldset {
  border-color: var(--accent) !important;
}
.premium-input.v-input--is-focused >>> fieldset {
  border-width: 1.5px !important;
  border-color: var(--accent) !important;
}
.premium-input >>> .v-input__prepend-inner {
  margin-top: 8px !important;
  margin-right: 8px !important;
}
.premium-input >>> input {
  font-size: 14px !important;
  font-family: "Inter", sans-serif !important;
  color: var(--text-primary) !important;
}

/* Autofill styling fix */
.premium-input >>> input:-webkit-autofill,
.premium-input >>> input:-webkit-autofill:hover, 
.premium-input >>> input:-webkit-autofill:focus,
.premium-input >>> input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px var(--bg-primary) inset !important;
  -webkit-text-fill-color: var(--text-primary) !important;
  transition: background-color 5000s ease-in-out 0s;
}

/* Error States */
.premium-input.error--text >>> fieldset {
  border-color: #ef4444 !important;
  border-width: 1px !important;
}

.premium-input >>> .v-messages {
  min-height: 14px;
  padding-top: 4px;
}

.premium-input >>> .v-messages__message {
  color: #ef4444 !important;
  font-size: 11.5px !important;
  font-weight: 500;
  line-height: 1.2;
  font-family: "Inter", sans-serif;
  letter-spacing: -0.01em;
}

/* Shake animation for errors */
.has-error {
  animation: ism-error-shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
}

/* Searchable autocomplete placeholder overlay */
.autocomplete-wrapper {
  position: relative;
}
.autocomplete-placeholder {
  position: absolute;
  top: 50%;
  /* offset right of prepend icon (20px icon + 8px mr + 14px left padding) */
  left: 42px;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 14px;
  font-family: "Inter", sans-serif;
  color: rgba(0, 0, 0, 0.42);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 1;
}

@keyframes ism-error-shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-3px, 0, 0); }
  40%, 60% { transform: translate3d(3px, 0, 0); }
}
</style>
